import React, { useEffect, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  Filter, X, Globe, TrendingUp, AlertTriangle, FileText, Calendar, DollarSign,
  ExternalLink, MapPin, Info, SlidersHorizontal, Check, ChevronRight, ShieldCheck
} from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Status (gare) → human-readable label.
function statusLabel(status) {
  if (status === "aperta") return "Gara Aperta";
  if (status === "in_scadenza") return "In Scadenza";
  return "Scaduta";
}

// Status "aperta" | "in_scadenza" | other  → colored dot for tenders.
function statusColor(status) {
  if (status === "aperta") return "#00FF66";
  if (status === "in_scadenza") return "#FF9F0A";
  return "#FF3B30";
}

// Score → generic risk label "Alto" | "Medio" | "Basso".
function scoreLabel(score) {
  if (score >= 70) return "Alto";
  if (score >= 40) return "Medio";
  return "Basso";
}

// Score → color gradient green→yellow→red
function scoreColor(score, invertLogic = false) {
  if (invertLogic) {
    // Tender: green = opportunity
    return "#00FF66";
  }
  if (score >= 70) return "#FF3B30";
  if (score >= 40) return "#FF9F0A";
  return "#00FF66";
}

const TYPE_META = {
  dogane: {
    titleKey: "menu.customs",
    subtitleKey: "ctx.customs.subtitle2",
    scoreLabelKey: "ctx.customs.complexity",
    invertLogic: false,
    legendKeys: [
      { color: "#00FF66", labelKey: "ctx.legend.low_complexity" },
      { color: "#FF9F0A", labelKey: "ctx.legend.medium_complexity" },
      { color: "#FF3B30", labelKey: "ctx.legend.high_complexity" },
    ],
  },
  finanziaria: {
    titleKey: "menu.finance",
    subtitleKey: "ctx.finance.subtitle2",
    scoreLabelKey: "ctx.finance.risk",
    invertLogic: false,
    legendKeys: [
      { color: "#00FF66", labelKey: "ctx.legend.low_risk" },
      { color: "#FF9F0A", labelKey: "ctx.legend.medium_risk" },
      { color: "#FF3B30", labelKey: "ctx.legend.high_risk" },
    ],
  },
  commerciale: {
    titleKey: "menu.commerce",
    subtitleKey: "ctx.commerce.subtitle2",
    scoreLabelKey: "ctx.commerce.ease",
    invertLogic: false,
    legendKeys: [
      { color: "#00FF66", labelKey: "ctx.legend.high_ease" },
      { color: "#FF9F0A", labelKey: "ctx.legend.medium_ease" },
      { color: "#FF3B30", labelKey: "ctx.legend.low_ease" },
    ],
  },
  gare: {
    titleKey: "menu.tenders",
    subtitleKey: "ctx.tenders.subtitle2",
    scoreLabelKey: "ctx.tenders.opportunity",
    invertLogic: true,
    legendKeys: [
      { color: "#00FF66", labelKey: "ctx.legend.open_tender" },
      { color: "#FF9F0A", labelKey: "ctx.legend.expiring" },
      { color: "#FF3B30", labelKey: "ctx.legend.closed_tender" },
    ],
  },
};

const SECTORS = ["Tutti", "IT", "Healthcare", "Energia", "Infrastrutture", "Difesa", "Trasporti", "Educazione"];

function MarkerTooltipContent({ marker, type }) {
  if (type === "gare") {
    return (
      <div className="text-xs space-y-1">
        <p className="font-bold text-white">{marker.name}</p>
        <p className="text-[#CBD5E1]">{marker.country}</p>
        {marker.sector && <p className="text-[#00E5FF]">Settore: {marker.sector}</p>}
        {marker.deadline && <p className="text-[#FF9F0A]">Scadenza: {marker.deadline}</p>}
        {marker.amount && <p className="text-[#00FF66]">Importo: {marker.amount}</p>}
        {marker.link && <p className="text-[#00E5FF] underline text-[10px]">Vedi bando</p>}
      </div>
    );
  }
  return (
    <div className="text-xs space-y-1">
      <p className="font-bold text-white">{marker.country}</p>
      <p className="text-[#CBD5E1]">Score: <span className="font-bold text-white">{marker.score}</span></p>
      {marker.description && <p className="text-[#CBD5E1] max-w-[200px]">{marker.description}</p>}
      {marker.note && <p className="text-[#FF9F0A] max-w-[200px]">{marker.note}</p>}
    </div>
  );
}

const MARKINGS = [
  { id: "CE",   label: "CE",   region: "Europa",       countries: ["Italia","France","Germania","Spagna","Portogallo","Belgio","Paesi Bassi","Austria","Polonia","Repubblica Ceca","Ungheria","Danimarca","Finlandia","Svezia","Grecia","Irlanda","Slovenia","Croazia","Romania","Bulgaria","Estonia","Lettonia","Lituania","Slovacchia","Cipro","Malta","Norvegia","Islanda","Lussemburgo","EU","Europe","Italy","France","Germany","Spain","Portugal","Belgium","Netherlands","Poland","Czech Republic","Hungary","Denmark","Finland","Sweden","Greece","Ireland","Slovenia","Croatia","Romania","Bulgaria","Estonia","Latvia","Lithuania","Slovakia","Cyprus","Malta","Norway","Iceland","Luxembourg"] },
  { id: "UKCA", label: "UKCA", region: "Regno Unito",   countries: ["Regno Unito","UK","United Kingdom","England","Scotland","Wales","Northern Ireland"] },
  { id: "CSA",  label: "CSA",  region: "Canada",        countries: ["Canada"] },
  { id: "UL",   label: "UL",   region: "USA",           countries: ["Stati Uniti","USA","United States","US"] },
  { id: "ETL",  label: "ETL",  region: "USA",           countries: ["Stati Uniti","USA","United States","US"] },
  { id: "PSE",  label: "PSE",  region: "Giappone",      countries: ["Giappone","Japan"] },
  { id: "RCM",  label: "RCM",  region: "Australia/NZ",  countries: ["Australia","Nuova Zelanda","New Zealand"] },
  { id: "CCC",  label: "CCC",  region: "Cina",          countries: ["Cina","China","China Mainland"] },
  { id: "KC",   label: "KC",   region: "Corea del Sud", countries: ["Corea","Corea del Sud","South Korea","Korea"] },
  { id: "BIS",  label: "BIS",  region: "India",         countries: ["India"] },
  { id: "SASO", label: "SASO", region: "Arabia Saudita", countries: ["Arabia Saudita","Saudi Arabia"] },
  { id: "INMETRO", label: "INMETRO", region: "Brasile", countries: ["Brasile","Brazil"] },
];

function getMarkings(country) {
  if (!country) return [];
  const out = [];
  for (const m of MARKINGS) {
    if (m.countries.some(c => country.toLowerCase().includes(c.toLowerCase()))) out.push(m.id);
  }
  return out;
}

export default function NormativaPage({ type, embedded = false }) {
  const { t } = useLanguage();
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectorFilter, setSectorFilter] = useState("Tutti");
  const [selected, setSelected] = useState(null);
  const [riskFilter, setRiskFilter] = useState(new Set()); // empty = tutti
  const [markingFilter, setMarkingFilter] = useState(new Set()); // empty = tutti
  const [hoveredId, setHoveredId] = useState(null);
  const meta = TYPE_META[type] || TYPE_META.dogane;

  const hasActiveFilter =
    riskFilter.size > 0 ||
    markingFilter.size > 0 ||
    (type === "gare" && sectorFilter !== "Tutti");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/normativa/${type}/markers`, { withCredentials: true })
      .then(r => setMarkers(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [type]);

  const filtered = React.useMemo(() => {
    let out = markers;
    // Filtro settore (solo gare)
    if (type === "gare" && sectorFilter !== "Tutti") {
      out = out.filter(m => m.sector === sectorFilter);
    }
    // Filtro Rischio (solo non-gare)
    if (type !== "gare" && riskFilter.size > 0) {
      out = out.filter(m => {
        const s = m.score;
        if (riskFilter.has("high") && s >= 70) return true;
        if (riskFilter.has("mid") && s >= 40 && s < 70) return true;
        if (riskFilter.has("low") && s < 40) return true;
        return false;
      });
    }
    // Filtro Marchiatura (tutti i tipi)
    if (markingFilter.size > 0) {
      out = out.filter(m => {
        const marks = getMarkings(m.country || "");
        for (const mk of marks) if (markingFilter.has(mk)) return true;
        return false;
      });
    }
    return out;
  }, [markers, type, sectorFilter, riskFilter, markingFilter]);

  const stats = type === "gare"
    ? { total: markers.length, open: markers.filter(m => m.status === "aperta").length, amount: markers.reduce((s, m) => s + (m.amount_raw || 0), 0) }
    : { high: markers.filter(m => m.score >= 70).length, med: markers.filter(m => m.score >= 40 && m.score < 70).length, low: markers.filter(m => m.score < 40).length };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Top bar (nascosto quando embedded) */}
      {!embedded && (
      <div className="px-6 py-4 border-b border-white/[0.04] bg-[#0A0C10] flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              {t(meta.titleKey)}
            </h1>
            <p className="text-xs text-[#A0AEC0] mt-0.5 tracking-widest uppercase">{t(meta.subtitleKey)}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {type === "gare" && (
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-[#A0AEC0]" />
                <select
                  value={sectorFilter}
                  onChange={e => setSectorFilter(e.target.value)}
                  className="bg-[#050505] border border-white/[0.04] text-white px-3 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]"
                >
                  {SECTORS.map(s => <option key={s} value={s}>{s === "Tutti" ? t("ctx.sectors_all") : s}</option>)}
                </select>
              </div>
            )}
            {/* Legend */}
            <div className="flex items-center gap-3">
              {meta.legendKeys.map(l => (
                <div key={l.labelKey} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
                  <span className="text-[10px] text-[#A0AEC0]">{t(l.labelKey)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick stats */}
        <div className="flex gap-4 mt-3 flex-wrap">
          {type === "gare" ? (
            <>
              <div className="flex items-center gap-2"><span className="text-[10px] text-[#A0AEC0] uppercase tracking-widest">Gare totali:</span><span className="text-xs font-bold text-[#00E5FF]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{stats.total}</span></div>
              <div className="flex items-center gap-2"><span className="text-[10px] text-[#A0AEC0] uppercase tracking-widest">Aperte:</span><span className="text-xs font-bold text-[#00FF66]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{stats.open}</span></div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#FF3B30]" /><span className="text-[10px] text-[#A0AEC0]">Alto: <span className="text-white font-bold">{stats.high}</span></span></div>
              <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#FF9F0A]" /><span className="text-[10px] text-[#A0AEC0]">Medio: <span className="text-white font-bold">{stats.med}</span></span></div>
              <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#00FF66]" /><span className="text-[10px] text-[#A0AEC0]">Basso: <span className="text-white font-bold">{stats.low}</span></span></div>
            </>
          )}
        </div>
      </div>
      )}

      {/* Map + filtri + sidebar */}
      <div className="flex-1 flex overflow-hidden gap-3 p-3 bg-[#050709]">
        {/* Map */}
        <div className="flex-1 relative rounded-2xl overflow-hidden shadow-xl" style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.4)" }}>
          {!loading && (
            <MapContainer center={[30, 15]} zoom={2} style={{ width: "100%", height: "100%" }} minZoom={2}>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap &copy; CARTO'
              />
              {filtered.map((m, i) => {
                const mColor = type === "gare" ? statusColor(m.status) : scoreColor(m.score);
                const key = m.id || i;
                const isHover = hoveredId === key;
                // "Filtrato" = c'è almeno un filtro attivo (la lista `filtered`
                // contiene già solo item che passano i filtri quando ce ne sono).
                const isFiltered = hasActiveFilter;
                const isActive = isHover || isFiltered;
                return (
                  <CircleMarker
                    key={key}
                    center={[m.lat, m.lng]}
                    radius={isHover ? 8 : 6}
                    pathOptions={{
                      fillColor: isActive ? mColor : "#FFFFFF",
                      fillOpacity: isActive ? 0.9 : 0.85,
                      color: isActive ? mColor : "#94A3B8",
                      weight: isHover ? 2 : 1,
                    }}
                    eventHandlers={{
                      click: () => setSelected(m),
                      mouseover: () => setHoveredId(key),
                      mouseout: () => setHoveredId(prev => (prev === key ? null : prev)),
                    }}
                  >
                    <Tooltip
                      permanent={false}
                      className="normativa-tooltip"
                      opacity={1}
                    >
                      <div className="bg-[#0D0F12] border border-white/[0.04] p-3 min-w-[160px]">
                        <MarkerTooltipContent marker={m} type={type} />
                      </div>
                    </Tooltip>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          )}
        </div>

        {/* Sidebar destra: Filtri + Lista */}
        <div className="w-80 flex flex-col gap-3 overflow-hidden flex-shrink-0">
          {/* Card Filtri */}
          <div className="bg-[#0D0F12] border border-white/[0.06] rounded-xl shadow-lg">
            <div className="px-4 py-2.5 border-b border-white/[0.06] flex items-center gap-2">
              <SlidersHorizontal className="w-3.5 h-3.5 text-[#00E5FF]" />
              <p className="text-[11px] tracking-[0.2em] uppercase text-white font-bold">Filtri</p>
              {(riskFilter.size > 0 || markingFilter.size > 0 || sectorFilter !== "Tutti") && (
                <button
                  data-testid="compliance-filters-reset"
                  onClick={() => { setRiskFilter(new Set()); setMarkingFilter(new Set()); setSectorFilter("Tutti"); }}
                  className="ml-auto text-[9px] tracking-wider uppercase text-[#00E5FF] hover:text-white"
                >Reset</button>
              )}
            </div>

            {/* Rischio (solo non-gare) */}
            {type !== "gare" && (
              <details className="group border-b border-white/[0.06]">
                <summary className="list-none cursor-pointer px-4 py-2.5 flex items-center gap-2 hover:bg-[#12161C] transition-colors">
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#CBD5E1] font-semibold">Rischio</p>
                  <span className="ml-auto flex items-center gap-1.5">
                    <span className="text-[10px] text-white font-semibold">
                      {riskFilter.size === 0 ? "Tutti" : `${riskFilter.size} selez.`}
                    </span>
                    <ChevronRight className="w-3 h-3 text-[#8B9AB0] transition-transform group-open:rotate-90" />
                  </span>
                </summary>
                <div className="px-2 pb-3 pt-1 flex flex-col gap-1">
                  {[
                    { k: "high", color: "#FF3B30", label: "Alto (≥70)" },
                    { k: "mid",  color: "#FF9F0A", label: "Medio (40-69)" },
                    { k: "low",  color: "#00FF66", label: "Basso (<40)" },
                  ].map(opt => {
                    const active = riskFilter.has(opt.k);
                    return (
                      <button
                        key={opt.k}
                        data-testid={`compliance-risk-${opt.k}`}
                        onClick={() => setRiskFilter(prev => {
                          const next = new Set(prev);
                          if (next.has(opt.k)) next.delete(opt.k); else next.add(opt.k);
                          return next;
                        })}
                        className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-[11px] rounded-md transition-colors ${active ? "bg-[#12161C]" : "hover:bg-[#12161C]"}`}
                      >
                        <span
                          className="w-4 h-4 rounded-[3px] flex items-center justify-center flex-shrink-0 transition-colors"
                          style={{
                            backgroundColor: active ? opt.color : "transparent",
                            border: `1.5px solid ${active ? opt.color : "#3A4152"}`,
                          }}
                        >
                          {active && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                        </span>
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: opt.color, boxShadow: `0 0 4px ${opt.color}` }} />
                        <span className={`flex-1 text-left tracking-wider uppercase ${active ? "text-white font-semibold" : "text-[#CBD5E1]"}`}>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </details>
            )}

            {/* Settore (solo gare) */}
            {type === "gare" && (
              <details className="group border-b border-white/[0.06]" open>
                <summary className="list-none cursor-pointer px-4 py-2.5 flex items-center gap-2 hover:bg-[#12161C] transition-colors">
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#CBD5E1] font-semibold">Settore</p>
                  <span className="ml-auto flex items-center gap-1.5">
                    <span className="text-[10px] text-white font-semibold">
                      {sectorFilter === "Tutti" ? "Tutti" : sectorFilter}
                    </span>
                    <ChevronRight className="w-3 h-3 text-[#8B9AB0] transition-transform group-open:rotate-90" />
                  </span>
                </summary>
                <div className="px-2 pb-3 pt-1 flex flex-col gap-1 max-h-56 overflow-y-auto">
                  {SECTORS.map(s => {
                    const active = sectorFilter === s;
                    return (
                      <button
                        key={s}
                        onClick={() => setSectorFilter(s)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] rounded-md transition-colors text-left ${active ? "bg-[#00E5FF]/15 text-white font-semibold" : "text-[#CBD5E1] hover:bg-[#12161C]"}`}
                      >
                        {s === "Tutti" ? t("ctx.sectors_all") : s}
                      </button>
                    );
                  })}
                </div>
              </details>
            )}

            {/* Marchiatura */}
            <details className="group">
              <summary className="list-none cursor-pointer px-4 py-2.5 flex items-center gap-2 hover:bg-[#12161C] transition-colors">
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#CBD5E1] font-semibold">Marchiatura</p>
                <span className="ml-auto flex items-center gap-1.5">
                  <span className="text-[10px] text-white font-semibold">
                    {markingFilter.size === 0 ? "Tutti" : `${markingFilter.size} selez.`}
                  </span>
                  <ChevronRight className="w-3 h-3 text-[#8B9AB0] transition-transform group-open:rotate-90" />
                </span>
              </summary>
              <div className="px-2 pb-3 pt-1 flex flex-col gap-1 max-h-64 overflow-y-auto">
                {MARKINGS.map(mk => {
                  const active = markingFilter.has(mk.id);
                  return (
                    <button
                      key={mk.id}
                      data-testid={`compliance-marking-${mk.id}`}
                      onClick={() => setMarkingFilter(prev => {
                        const next = new Set(prev);
                        if (next.has(mk.id)) next.delete(mk.id); else next.add(mk.id);
                        return next;
                      })}
                      className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-[11px] rounded-md transition-colors ${active ? "bg-[#12161C]" : "hover:bg-[#12161C]"}`}
                    >
                      <span
                        className="w-4 h-4 rounded-[3px] flex items-center justify-center flex-shrink-0 transition-colors"
                        style={{
                          backgroundColor: active ? "#00E5FF" : "transparent",
                          border: `1.5px solid ${active ? "#00E5FF" : "#3A4152"}`,
                        }}
                      >
                        {active && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                      </span>
                      <ShieldCheck className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0" />
                      <span className="flex-1 text-left">
                        <span className={`tracking-wider font-mono font-bold ${active ? "text-white" : "text-[#CBD5E1]"}`}>{mk.label}</span>
                        <span className="text-[9px] text-[#6B7280] ml-1.5">{mk.region}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </details>
          </div>

          {/* Sidebar list */}
          <div className="bg-[#0D0F12] border border-white/[0.06] rounded-xl shadow-lg flex-1 overflow-hidden flex flex-col">
            <div className="px-4 py-2.5 border-b border-white/[0.06] flex-shrink-0">
              <p className="text-[11px] tracking-[0.2em] uppercase text-white font-bold">Elementi <span className="text-[#6B7280] font-normal">· {filtered.length}</span></p>
            </div>
            <div className="flex-1 overflow-y-auto">
          {filtered.map((m, i) => {
            const mColor = type === "gare" ? statusColor(m.status) : scoreColor(m.score);
            return (
              <div key={m.id || i} data-testid="normativa-row"
                onClick={() => setSelected(m)}
                className="px-4 py-3 border-b border-white/[0.04] hover:bg-[#1A1D24] transition-colors cursor-pointer">
                <div className="flex items-start gap-2">
                  <div className="w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: mColor }} />
                  <div className="flex-1 min-w-0">
                    {type === "gare" ? (
                      <>
                        <p className="text-xs text-white font-medium leading-snug">{m.name}</p>
                        <p className="text-[10px] text-[#CBD5E1] mt-0.5">{m.country}</p>
                        {m.sector && <span className="text-[10px] px-1.5 py-0.5 bg-[#1A1D24] border border-white/[0.04] text-[#A0AEC0] mt-1 inline-block">{m.sector}</span>}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {m.deadline && <span className="text-[10px] text-[#FF9F0A]">Scad. {m.deadline}</span>}
                          {m.amount && <span className="text-[10px] text-[#00FF66]">{m.amount}</span>}
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-white font-medium">{m.country}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-[#A0AEC0]">{t(meta.scoreLabelKey)}:</span>
                          <span className="text-xs font-bold" style={{ color: mColor, fontFamily: "JetBrains Mono, monospace" }}>{m.score}</span>
                        </div>
                        {m.description && <p className="text-[10px] text-[#A0AEC0] mt-1 leading-relaxed">{m.description}</p>}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
            </div>{/* flex-1 overflow-y-auto */}
          </div>{/* card lista */}
        </div>{/* sidebar destra */}
      </div>{/* flex-1 flex overflow-hidden */}

      {selected && <NormativaDetailPanel marker={selected} type={type} meta={meta} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ─── Detail Panel (War Room style slide-over) ──────────────────────────────
function NormativaDetailPanel({ marker, type, meta, onClose }) {
  const { t } = useLanguage();
  const isGare = type === "gare";
  const mColor = isGare ? statusColor(marker.status) : scoreColor(marker.score);
  const scoreLbl = isGare ? null : scoreLabel(marker.score);

  return (
    <div data-testid="normativa-detail-panel" className="fixed inset-0 flex" style={{ zIndex: 9999 }}>
      <div className="flex-1 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div
        className="h-full w-full max-w-[640px] bg-[#0A0C10] border-l-2 flex flex-col overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.6)]"
        style={{ borderLeftColor: mColor }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/[0.04] bg-[#0D0F12]">
          <div className="w-12 h-12 flex items-center justify-center border-2 flex-shrink-0"
            style={{ borderColor: mColor, backgroundColor: `${mColor}1A` }}>
            <Globe className="w-6 h-6" style={{ color: mColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white truncate" style={{ fontFamily: "Chivo, sans-serif" }}>
              {isGare ? marker.name : marker.country}
            </h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[10px] px-2 py-0.5 border font-bold tracking-widest uppercase"
                style={{ color: mColor, borderColor: mColor, backgroundColor: `${mColor}15` }}>
                {t(meta.titleKey)}
              </span>
              {isGare && marker.country && <span className="text-xs text-[#CBD5E1] flex items-center gap-1"><MapPin className="w-3 h-3" />{marker.country}</span>}
              {isGare && marker.sector && <span className="text-xs text-[#A78BFA]">· {marker.sector}</span>}
              {!isGare && scoreLbl && <span className="text-xs text-[#CBD5E1]">Rischio {scoreLbl}</span>}
            </div>
          </div>
          <button data-testid="normativa-panel-close" onClick={onClose}
            className="text-[#CBD5E1] hover:text-white hover:bg-[#1F242D] p-2 transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {isGare ? (
            <>
              {/* Status banner per gare */}
              <section className="relative overflow-hidden bg-[#0D0F12] border p-5 rounded-xl" style={{ borderColor: `${mColor}40` }}>
                <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 100% 0%, ${mColor} 0%, transparent 50%)` }} />
                <div className="relative flex items-center gap-4">
                  <div className="flex-1">
                    <p className="text-[11px] tracking-[0.15em] uppercase font-semibold" style={{ color: mColor }}>Status</p>
                    <p className="text-2xl font-black mt-1 capitalize" style={{ fontFamily: "Chivo, sans-serif" }}>
                      {statusLabel(marker.status)}
                    </p>
                  </div>
                  {marker.deadline && (
                    <div className="text-right">
                      <p className="text-[11px] tracking-[0.15em] uppercase text-[#A0AEC0]">Scadenza</p>
                      <p className="text-base font-bold text-white mt-1 flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" style={{ color: mColor }} />{marker.deadline}
                      </p>
                    </div>
                  )}
                </div>
              </section>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {marker.amount && (
                  <div className="bg-[#0D0F12] border border-white/[0.04] p-4 rounded-xl">
                    <p className="text-[11px] text-[#A0AEC0] tracking-[0.15em] uppercase flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" />Importo</p>
                    <p className="text-xl font-bold text-[#00FF66] mt-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>{marker.amount}</p>
                  </div>
                )}
                {marker.sector && (
                  <div className="bg-[#0D0F12] border border-white/[0.04] p-4 rounded-xl">
                    <p className="text-[11px] text-[#A0AEC0] tracking-[0.15em] uppercase">Settore</p>
                    <p className="text-base font-semibold text-[#A78BFA] mt-2">{marker.sector}</p>
                  </div>
                )}
              </div>

              {marker.description && (
                <section className="bg-[#0D0F12] border border-white/[0.04] p-5 rounded-xl">
                  <p className="text-[11px] text-[#A0AEC0] tracking-[0.15em] uppercase font-semibold mb-3 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />Descrizione</p>
                  <p className="text-sm text-[#D1D5DB] leading-relaxed whitespace-pre-wrap">{marker.description}</p>
                </section>
              )}

              {marker.note && (
                <section className="bg-[#FF9F0A]/5 border border-[#FF9F0A]/30 p-5 rounded-xl">
                  <p className="text-[11px] text-[#FF9F0A] tracking-[0.15em] uppercase font-semibold mb-3 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />Nota operativa</p>
                  <p className="text-sm text-[#FED7AA] leading-relaxed">{marker.note}</p>
                </section>
              )}

              {marker.link && (
                <a href={marker.link} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF] text-sm font-bold tracking-wider uppercase hover:bg-[#00E5FF]/20 transition-all rounded-xl">
                  <ExternalLink className="w-4 h-4" /> Vai al bando ufficiale
                </a>
              )}
            </>
          ) : (
            <>
              {/* Score hero per normativa */}
              <section className="relative overflow-hidden bg-[#0D0F12] border p-6 rounded-xl" style={{ borderColor: `${mColor}40` }}>
                <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 100% 0%, ${mColor} 0%, transparent 50%)` }} />
                <div className="relative">
                  <p className="text-[11px] tracking-[0.15em] uppercase font-semibold" style={{ color: mColor }}>
                    {t(meta.scoreLabelKey)}
                  </p>
                  <div className="flex items-end gap-3 mt-2">
                    <p className="text-6xl font-black leading-none" style={{ fontFamily: "Chivo, sans-serif", color: mColor }}>
                      {marker.score}
                    </p>
                    <p className="text-sm text-[#CBD5E1] mb-2">/ 100</p>
                  </div>
                  <div className="mt-4 h-2 w-full bg-[#1F242D] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${marker.score}%`, backgroundColor: mColor }} />
                  </div>
                  <div className="flex justify-between mt-2 text-[10px] text-[#A0AEC0] tracking-widest uppercase">
                    <span>Basso</span><span>Medio</span><span>Alto</span>
                  </div>
                </div>
              </section>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-[#0D0F12] border border-white/[0.04] p-4 rounded-xl">
                  <p className="text-[11px] text-[#A0AEC0] tracking-[0.15em] uppercase flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />Paese</p>
                  <p className="text-base font-semibold text-white mt-2">{marker.country}</p>
                </div>
                <div className="bg-[#0D0F12] border border-white/[0.04] p-4 rounded-xl">
                  <p className="text-[11px] text-[#A0AEC0] tracking-[0.15em] uppercase flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" />Coordinate</p>
                  <p className="text-sm font-mono text-[#CBD5E1] mt-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    {marker.lat?.toFixed(2)}, {marker.lng?.toFixed(2)}
                  </p>
                </div>
              </div>

              {marker.description && (
                <section className="bg-[#0D0F12] border border-white/[0.04] p-5 rounded-xl">
                  <p className="text-[11px] text-[#A0AEC0] tracking-[0.15em] uppercase font-semibold mb-3 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />Contesto normativo</p>
                  <p className="text-sm text-[#D1D5DB] leading-relaxed whitespace-pre-wrap">{marker.description}</p>
                </section>
              )}

              {marker.note && (
                <section className="bg-[#00E5FF]/5 border border-[#00E5FF]/30 p-5 rounded-xl">
                  <p className="text-[11px] text-[#00E5FF] tracking-[0.15em] uppercase font-semibold mb-3 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" />Insight operativo</p>
                  <p className="text-sm text-[#A5F3FC] leading-relaxed">{marker.note}</p>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
